import { Pressable, StyleSheet, View } from 'react-native';
import { X } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import type { KnowledgeGraphNodeViewModel } from '../../types/learningAnalysis';
import { AppButton } from '../ui/AppButton';
import { AppText } from '../ui/AppText';

type KnowledgeGraphDetailSheetProps = {
  node: KnowledgeGraphNodeViewModel | null;
  onClose?: () => void;
  onAction?: (node: KnowledgeGraphNodeViewModel) => void;
};

export function KnowledgeGraphDetailSheet({
  node,
  onClose,
  onAction,
}: KnowledgeGraphDetailSheetProps) {
  if (!node) {
    return null;
  }

  return (
    <View style={styles.sheet}>
      <View style={styles.header}>
        <View style={styles.titleCopy}>
          <AppText variant="subtitle" weight="bold">
            {node.label}
          </AppText>
          <AppText variant="caption" tone="muted">
            {node.subject} · {node.statusLabel}
          </AppText>
        </View>
        {onClose ? (
          <Pressable accessibilityRole="button" onPress={onClose} style={styles.closeButton}>
            <X size={16} color={tokens.colors.textMuted} />
          </Pressable>
        ) : null}
      </View>

      <View style={styles.metricRow}>
        <View style={styles.metricCard}>
          <AppText variant="metric" weight="bold" tone="primary">
            {node.masteryPercent}
          </AppText>
          <AppText variant="caption" tone="muted">
            掌握度
          </AppText>
        </View>
        <View style={styles.descriptionCard}>
          <AppText variant="body" tone="muted">
            {node.description ?? '结合最近练习记录，优先复盘关联错题和关键步骤。'}
          </AppText>
        </View>
      </View>

      {onAction ? (
        <AppButton
          label={node.actionLabel ?? '进入专项练习'}
          onPress={() => onAction(node)}
          style={styles.actionButton}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  sheet: {
    gap: tokens.spacing.md,
    borderRadius: tokens.radius.lg,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.md,
  },
  titleCopy: {
    flex: 1,
    gap: 2,
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  metricRow: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  metricCard: {
    width: 88,
    minHeight: 88,
    borderRadius: tokens.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  descriptionCard: {
    flex: 1,
    minHeight: 88,
    borderRadius: tokens.radius.md,
    justifyContent: 'center',
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.surface,
  },
  actionButton: {
    minHeight: 42,
  },
});
